import React, { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { forgotPassword } from "../api/auth";
import Particles from 'react-tsparticles';
import { loadSlim } from "tsparticles-slim";

import {
    Container, Box, Typography, TextField, Button, Alert, Paper,
    Stack, CircularProgress, InputAdornment, Fade, Link as MuiLink, Avatar
} from "@mui/material";

// Icons for the page
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined';

export default function ForgotPasswordPage() {
    const [email, setEmail] = useState("");
    const [msg, setMsg] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    // --- START: ADDED PARTICLE CONFIGURATION ---
    // This configuration is now fully included, as seen in your LoginPage.
    const particlesConfig = {
        particles: {
            number: { value: 40, density: { enable: true, value_area: 800 } },
            color: { value: ["#43a047", "#ffc400", "#2196f3"] },
            shape: { type: "circle" },
            opacity: { value: 0.5, random: true },
            size: { value: 3, random: true },
            move: { enable: true, speed: 1, direction: "none", random: false, straight: false, outModes: "out" }
        },
        interactivity: {
            events: {
                onHover: { enable: true, mode: "grab" },
                onClick: { enable: true, mode: "push" }
            },
            modes: {
                grab: { distance: 140, links: { opacity: 1 } },
                push: { quantity: 4 }
            }
        },
        background: { color: { value: "transparent" } }
    };

    const particlesInit = async (engine) => {
        await loadSlim(engine);
    };
    // --- END: ADDED PARTICLE CONFIGURATION ---

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setMsg("");
        setError("");
        try {
            console.debug('[FE][Forgot] Submitting forgot-password with email', email);
            await forgotPassword({ email });
            console.debug('[FE][Forgot] Forgot-password submitted successfully');
            setMsg("Success! If an account with that email exists, a password reset link has been sent.");
        } catch (err) {
            console.error('[FE][Forgot] Forgot-password failed', err);
            // Still show generic success to prevent email enumeration
            setMsg("Success! If an account with that email exists, a password reset link has been sent.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ position: 'relative', overflow: 'hidden', backgroundColor: '#f8faf8', minHeight: 'calc(100vh - 128px)' }}>
            {/* The Particles component now has its configuration and will render correctly. */}
            <Particles
                id="tsparticles"
                init={particlesInit}
                options={particlesConfig}
                style={{ position: 'absolute', width: '100%', height: '100%', zIndex: 0 }}
            />
            <Container
                maxWidth="lg"
                sx={{
                    position: 'relative', zIndex: 1, display: 'flex', flexDirection: { xs: 'column', md: 'row' },
                    justifyContent: 'space-between', alignItems: 'center', minHeight: '100vh', py: { xs: 4, md: 8 }
                }}
            >
                {/* Left side - Reassuring Message and Steps */}
                <Fade in={true} timeout={1000}>
                    <Box sx={{ maxWidth: 450, mb: { xs: 5, md: 0 }, display: { xs: 'none', md: 'block' }, p: 4, borderRadius: 4, backdropFilter: 'blur(8px)', backgroundColor: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(255, 255, 255, 0.2)', boxShadow: '0 8px 32px rgba(31, 38, 135, 0.1)' }}>
                        <Typography variant="h3" fontWeight={700} gutterBottom sx={{ backgroundImage: 'linear-gradient(90deg, #1b5e20, #43a047, #66bb6a)', backgroundSize: '200% auto', backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', animation: 'gradient 3s ease infinite' }}>
                            Let's Get You Back In
                        </Typography>
                        <Typography variant="h5" color="primary.dark" fontWeight={600} sx={{ mb: 3 }}>
                            Follow these simple steps to securely recover your account.
                        </Typography>

                        {[
                            { icon: <EmailOutlinedIcon />, text: "1. Enter your registered email address." },
                            { icon: <MarkEmailReadOutlinedIcon />, text: "2. Check your inbox for a secure link." },
                            { icon: <VpnKeyOutlinedIcon />, text: "3. Create your new, strong password." }
                        ].map((item, index) => (
                            <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.light', mr: 1.5 }}>
                                    {item.icon}
                                </Avatar>
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                    {item.text}
                                </Typography>
                            </Box>
                        ))}

                        <Box sx={{ display: 'flex', alignItems: 'center', backgroundColor: 'rgba(67, 160, 71, 0.1)', borderRadius: 2, p: 2, mt: 4 }}>
                            <VerifiedUserIcon color="primary" sx={{ mr: 1.5 }} />
                            <Typography variant="body2" color="text.secondary" fontWeight={500}>
                                Your account security is our top priority.
                            </Typography>
                        </Box>
                    </Box>
                </Fade>

                {/* Right side - Forgot Password Form */}
                <Fade in={true} timeout={1200}>
                    <Paper
                        elevation={4}
                        sx={{
                            p: { xs: 3, sm: 5 }, maxWidth: 420, width: '100%', mx: { xs: 2, md: 0 },
                            backgroundColor: 'rgba(255, 255, 255, 0.9)', backdropFilter: 'blur(10px)',
                            borderRadius: '24px', boxShadow: '0 8px 40px rgba(67, 160, 71, 0.15)',
                        }}
                    >
                        <Stack alignItems="center" spacing={2}>
                            <Avatar sx={{ mb: 1, bgcolor: 'primary.main', width: 56, height: 56 }}>
                                <HelpOutlineIcon sx={{ fontSize: 32 }} />
                            </Avatar>
                            <Typography component="h1" variant="h4" fontWeight={700} color="text.primary">
                                Forgot Password?
                            </Typography>
                            {msg && <Alert severity="success" sx={{ width: '100%' }}>{msg}</Alert>}
                            <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%', mt: 1 }}>
                                <Stack spacing={2.5}>
                                    <TextField fullWidth label="Email Address" name="email" type="email" value={email} onChange={e => setEmail(e.target.value)} required InputProps={{ startAdornment: (<InputAdornment position="start"><EmailOutlinedIcon sx={{ color: 'text.secondary' }} /></InputAdornment>) }} />
                                    <Button fullWidth type="submit" variant="contained" size="large" disabled={loading} sx={{ py: 1.5, background: 'linear-gradient(90deg, #43a047 30%, #66bb6a 90%)' }}>
                                        {loading ? <CircularProgress size={26} color="inherit" /> : 'Send Reset Link'}
                                    </Button>
                                </Stack>
                            </Box>
                            <MuiLink component={RouterLink} to="/login" variant="body2" sx={{ color: 'text.secondary', pt: 2, fontWeight: 500 }}>
                                &larr; Back to Login
                            </MuiLink>
                        </Stack>
                    </Paper>
                </Fade>
            </Container>
        </Box>
    );
}